import React, { useRef, useState, useEffect } from "react";
import "./index.scss";
import MdaeButton from "../button";
import { getUserPreference, setUserPreference } from "../../../utils/commom-utils";

interface IResizable {
  resizableContentPosition?: "left" | "right";
  resizableContent: React.ReactNode;
  children?: React.ReactNode;
  minSize?: number;
  defaultSize?: number;
  componentKey?: string;
  storeSizeInUserPreferences?: boolean;
  stickyToggle?: boolean;
}

const Resizable: React.FC<IResizable> = ({
  resizableContentPosition = "left",
  resizableContent,
  children,
  minSize = 120,
  defaultSize = 300,
  componentKey,
  storeSizeInUserPreferences,
  stickyToggle,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [isSectionOpened, setSectionOpened] = useState(true);
  const [size, setSize] = useState(defaultSize);
  const [panelWidth, setPanelWidth] = useState(defaultSize);
  const side = resizableContentPosition;
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const toggleWrapperRef = useRef<HTMLDivElement>(null);
  const toggleOffsetRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    if (storeSizeInUserPreferences && componentKey) {
      getUserPreference(componentKey).then((stored) => {
        const val = stored && stored.settings ? Number(stored.settings) : NaN;
        if (isMounted && !isNaN(val)) setSize(val);
      });
      getUserPreference(componentKey + "_IS_OPENED").then((stored) => {
        const val = stored && stored.settings ? stored.settings : null;
        if (isMounted && val) setSectionOpened(val === "true");
      });
    }
    return () => { isMounted = false; };
  }, [storeSizeInUserPreferences, componentKey, defaultSize]);

  useEffect(() => {
    if (storeSizeInUserPreferences && componentKey) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setUserPreference(componentKey, size);
      }, 300);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [size, storeSizeInUserPreferences, componentKey]);

  useEffect(() => {
    setPanelWidth(isSectionOpened ? size : 0);
  }, [isSectionOpened, size]);

  useEffect(() => {
    if (isSectionOpened) setPanelWidth(size);
  }, [size, isSectionOpened]);

  const handleMouseDown = () => setDragging(true);
  const handleMouseUp = () => setDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let newSize = side === "left" ? e.clientX - rect.left : rect.right - e.clientX;
    newSize = Math.max(minSize, Math.min(newSize, rect.width - minSize));
    setSize(newSize);
  };

  const handleToggle = () => {
    // Animate open/close by setting width to 0 or size
    if (!isSectionOpened) setPanelWidth(size);
    else setPanelWidth(0);
    if (componentKey) setUserPreference(componentKey + "_IS_OPENED", !isSectionOpened);
    setSectionOpened(!isSectionOpened);
  };

  useEffect(() => {
    if (!stickyToggle) return;
    const wrapper = toggleWrapperRef.current;
    if (!wrapper) return;

    let rafId = 0;

    const start = () => {
      const overlayContent = wrapper.closest('.dx-overlay-content');
      const headerEl = overlayContent?.querySelector('.dx-popup-title') as HTMLElement;
      if (!headerEl) return;

      const tick = () => {
        const headerBottom = headerEl.getBoundingClientRect().bottom;
        const naturalTop = wrapper.getBoundingClientRect().top - toggleOffsetRef.current;
        const needed = Math.max(0, headerBottom - naturalTop);
        if (Math.abs(needed - toggleOffsetRef.current) > 0.5) {
          toggleOffsetRef.current = needed;
          wrapper.style.transform = needed > 0 ? `translateY(${needed}px)` : '';
        }
        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
    };

    const timeoutId = setTimeout(start, 100);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
      wrapper.style.transform = '';
      toggleOffsetRef.current = 0;
    };
  }, [stickyToggle]);

  const renderPanel = () => (
    <div
      className="sys-mdae-panel sys-mdae-fixed-panel"
      style={{
        width: panelWidth,
        minWidth: 0,
        transition: "width 0.3s cubic-bezier(0.4,0,0.2,1)",
        overflow: "hidden",
        display: "block"
      }}
    >
      <div style={{ display: isSectionOpened || panelWidth > 0 ? "block" : "none" }}>
        {resizableContent}
      </div>
    </div>
  );

  const renderButtonAndHandle = () => (
    <>
      <div className="sys-mdae-resizable-toggle-wrapper" ref={toggleWrapperRef}>
        <MdaeButton
          id="sys-mdae-resizable-toggle-button"
          role="dxButton"
          icon={
            "application-section-primary-color fa fa-angle-double-" +
            (isSectionOpened ? "right" : "left")
          }
          hint={isSectionOpened ? "Close Section" : "Open Section"}
          stylingMode="contained"
          style={{ marginRight: 0, fontSize: 20 }}
          onClick={handleToggle}
        />
      </div>
      <div
        className="sys-mdae-drag-handle"
        style={{ display: isSectionOpened ? "block" : "none" }}
        onMouseDown={handleMouseDown}
      />
    </>
  );

  return (
    <div
      ref={containerRef}
      className="sys-mdae-resizable-container"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {side === "left" && renderPanel()}
      {side === "left" && renderButtonAndHandle()}
      <div className="sys-mdae-panel sys-mdae-flex-panel">
        <div>{children}</div>
      </div>
      {side === "right" && renderButtonAndHandle()}
      {side === "right" && renderPanel()}
    </div>
  );
};

export default Resizable;
